/**
 * Session Token Service
 * 
 * Manages anonymous session tokens for API abuse prevention.
 * Tokens are stored in sessionStorage to persist across page refreshes
 * but reset when the browser session ends.
 */

const SESSION_TOKEN_KEY = 'stocktable_session_token';
const SESSION_TOKEN_EXPIRY_KEY = 'stocktable_session_token_expiry';

// Session tokens expire after 10 minutes
const SESSION_TOKEN_TTL = 10 * 60 * 1000; // 10 minutes in milliseconds
// Refresh token if within 1 minute of expiration
const SESSION_TOKEN_REFRESH_THRESHOLD = 1 * 60 * 1000; // 1 minute in milliseconds

/**
 * Generate a unique session token
 */
function generateSessionToken(): string {
  // Generate a UUID v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create a session token
 * Returns the existing token if valid, or creates/refreshes a new one
 * Automatically refreshes if within 1 minute of expiration or expired
 */
export function getSessionToken(): string {
  try {
    // Check if we have a valid token in sessionStorage
    const existingToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
    const expiryTime = sessionStorage.getItem(SESSION_TOKEN_EXPIRY_KEY);
    
    if (existingToken && expiryTime) {
      const expiry = parseInt(expiryTime, 10);
      const now = Date.now();
      const timeUntilExpiry = expiry - now;
      
      // Check if token is expired or within 1 minute of expiration
      if (timeUntilExpiry <= SESSION_TOKEN_REFRESH_THRESHOLD) {
        // Token is expired or about to expire, generate a new one
        console.log(`🔑 Session token ${timeUntilExpiry <= 0 ? 'expired' : 'expiring soon'}, refreshing...`);
        const newToken = generateSessionToken();
        const newExpiry = now + SESSION_TOKEN_TTL;
        
        sessionStorage.setItem(SESSION_TOKEN_KEY, newToken);
        sessionStorage.setItem(SESSION_TOKEN_EXPIRY_KEY, newExpiry.toString());
        
        console.log('🔑 Generated new session token');
        return newToken;
      }
      
      // Token is still valid and not near expiration, return it
      return existingToken;
    }
    
    // No token exists, generate a new one
    const newToken = generateSessionToken();
    const now = Date.now();
    
    sessionStorage.setItem(SESSION_TOKEN_KEY, newToken);
    sessionStorage.setItem(SESSION_TOKEN_EXPIRY_KEY, (now + SESSION_TOKEN_TTL).toString());
    
    console.log('🔑 Generated new session token');
    return newToken;
  } catch (error) {
    // If sessionStorage is not available (e.g., in private browsing), generate a token in memory
    console.warn('⚠️ SessionStorage not available, using in-memory token');
    return generateSessionToken();
  }
}

/**
 * Clear the session token (useful for testing or logout)
 */
export function clearSessionToken(): void {
  try {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    sessionStorage.removeItem(SESSION_TOKEN_EXPIRY_KEY);
    console.log('🔑 Cleared session token');
  } catch (error) {
    console.warn('⚠️ Failed to clear session token:', error);
  }
}

/**
 * Check if a session token exists and is valid
 */
export function hasValidSessionToken(): boolean {
  try {
    const token = sessionStorage.getItem(SESSION_TOKEN_KEY);
    const expiryTime = sessionStorage.getItem(SESSION_TOKEN_EXPIRY_KEY);
    
    if (!token || !expiryTime) {
      return false;
    }
    
    const expiry = parseInt(expiryTime, 10);
    return Date.now() < expiry;
  } catch (error) {
    return false;
  }
}

